<script setup lang="ts">
import { computed, h, nextTick, reactive, ref, render, watch, watchEffect } from 'vue'
import { useGeoStore } from '@/stores/geo'
import { useLayersStore } from '@/stores/layers'
import { useMapsStore } from '@/stores/maps'
import { unrefElement, useWindowSize } from '@vueuse/core'
import bbox from '@turf/bbox'
import { useRouter, useRoute } from 'vue-router'

import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine'
import { pointerOutsideOfPreview } from '@atlaskit/pragmatic-drag-and-drop/element/pointer-outside-of-preview'
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview'
import { draggable, dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter'

import { Eye, EyeClosed, ScanEye, Trash2, Folder, Image, Waypoints, Ellipsis, ArrowUp, ArrowDown, Pencil, Check, ChevronDown, Map as MapIcon, Copy } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { toast } from '@/components/ui/toast'

// DATA_TYPE enum values (mirrors backend LayerDataType)
const DATA_TYPE_RASTER = 1
const DATA_TYPE_VECTOR = 2
const DATA_TYPE_GROUP = 3
import { Spinner } from '@/components/ui/spinner'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuShortcut,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger
} from '@/components/ui/context-menu'

const router = useRouter()
const route = useRoute()
const geoStore = useGeoStore()
const layersStore = useLayersStore()
const mapsStore = useMapsStore()
const emit = defineEmits(['deleteLayer'])
const props = defineProps<{
  item: any
  position: number
}>()

// children and drag previews never render as folders — group icon is handled separately via isGroup
const getDataTypeIcon = (dataType: number | undefined | null) => {
  if (dataType === DATA_TYPE_VECTOR) return Waypoints
  return Image
}

const orgId = props.item.properties.organization_id
const layerId = `layer-${props.item.uuid}`
const isTemp = computed(() => !!props.item.properties?.isTemp)

// geo/getMap = the active tile on the Maps tab; used only for Fly-To here.
const map = computed(() => geoStore.map)

const elRef = ref()
// Org-layers now render on the tiles (via MapLayers), not injected from this row.
const isDataLoaded = ref(true)
const isVisible = ref(props.item.properties?.visible ?? true)

const isSelected = computed(() => layersStore.isLayerSelected(orgId, props.item.uuid))
const selectedLayerCount = computed(() => layersStore.getSelectedLayerCount(orgId))
const isInGroup = computed(() => !!props.item.properties?.parent_id)

const children = computed(() => props.item.properties?.children || [])
// A layer behaves as a group in the UI only when it actually has children loaded.
// This prevents old backfilled data_type=3 LULC rows (no children yet) from showing a chevron.
const isGroup = computed(() => props.item.properties?.data_type === DATA_TYPE_GROUP && children.value.length > 0)
const expandedStorageKey = `layer-expanded-${props.item.uuid}`
const isExpanded = ref(localStorage.getItem(expandedStorageKey) === 'true')
watch(isExpanded, (val) => localStorage.setItem(expandedStorageKey, String(val)))

// Per-child visibility (starts matching parent visibility)
const childVisible = reactive<Record<string, boolean>>({})
watch(children, (kids) => {
  kids.forEach((child: any) => {
    if (!(child.uuid in childVisible)) {
      childVisible[child.uuid] = isVisible.value
    }
  })
}, { immediate: true })

const navigateToChild = (child: any) => {
  router.push({ name: 'LayerDetail', params: { org_id: orgId, layer_id: child.uuid } })
}

const toggleChildVisibility = (child: any) => {
  childVisible[child.uuid] = !childVisible[child.uuid]
}

// Sync local isVisible when the store value changes externally (e.g. from
// DetailTasking auto-enable). The tiles' MapLayers react to the store value.
watch(() => props.item.properties?.visible, (storeVisible) => {
  if (storeVisible === undefined || storeVisible === isVisible.value) return
  isVisible.value = storeVisible
})

const isHovered = ref(false)
const { width: windowWidth } = useWindowSize()
const isTouchLayout = computed(() => windowWidth.value < 1024)
const isDragging = ref(false)
const isDraggedOver = ref(false)
const dropPosition = ref<'above' | 'below' | 'inside' | null>(null)
const childElRefs = reactive<Record<string, HTMLElement | null>>({})
const childDragging = ref<string | null>(null)
const childDropState = ref<{ uuid: string; pos: 'above' | 'below' } | null>(null)
// A layer is a valid group drop target if it has data_type=group (regardless of children count)
const isDropTargetGroup = computed(() => props.item.properties?.data_type === DATA_TYPE_GROUP)

const layerIcon = computed(() => isGroup.value ? Folder : getDataTypeIcon(props.item.properties?.data_type))

const layerStatus = computed(() => {
  const status = props.item.properties?.layer?.status
  if (status === undefined || status === null) return null
  return parseInt(status)
})

const isProcessing = computed(() => {
  if (props.item.properties?.data_type === DATA_TYPE_GROUP) return false
  if (layerStatus.value !== null && layerStatus.value > 0) return true
  if (!isDataLoaded.value && isVisible.value && (layerStatus.value === null || layerStatus.value === 0)) return true
  return false
})

const toggleVisibility = ({ navigate = true } = {}) => {
  isVisible.value = !isVisible.value

  if (isGroup.value) {
    children.value.forEach((child: any) => {
      childVisible[child.uuid] = isVisible.value
    })
  }

  // Persist to the store — the tiles' MapLayers pick up the change reactively.
  const updatedLayer = {
    ...props.item,
    properties: {
      ...props.item.properties,
      visible: isVisible.value
    }
  }

  layersStore.updateOrgLayer({
    orgId,
    layer: updatedLayer
  })

  if (!navigate) return

  if (!isVisible.value && route.params.layer_id === props.item.uuid) {
    // Deselect layer (navigate away) when hiding the currently viewed layer
    router.push({
      name: 'OrgLayers',
      params: { org_id: orgId }
    })
  } else if (isVisible.value && !route.params.layer_id) {
    // Open layer detail when making visible and no layer page is open
    router.push({
      name: 'LayerDetail',
      params: { org_id: orgId, layer_id: props.item.uuid }
    })
  }
}

const showDeleteConfirm = ref(false)

// Inline rename state
const isEditing = ref(false)
const editingName = ref('')
const editInputRef = ref<HTMLInputElement | null>(null)

const startEditing = () => {
  isEditing.value = true
  editingName.value = props.item.properties?.name?.trim() || ''
  nextTick(() => {
    editInputRef.value?.focus()
    editInputRef.value?.select()
  })
}

const saveEdit = async () => {
  const trimmed = editingName.value.trim()
  if (!trimmed) {
    toast({ title: 'Layer name cannot be empty', variant: 'destructive' })
    return
  }

  const currentName = props.item.properties?.name?.trim() || ''
  if (trimmed === currentName) {
    cancelEdit()
    return
  }

  try {
    await layersStore.renameOrgLayer({
      orgId,
      layerId: props.item.uuid,
      name: trimmed
    })
    isEditing.value = false
  } catch (error) {
    toast({ title: 'Failed to rename layer', variant: 'destructive' })
  }
}

const cancelEdit = () => {
  isEditing.value = false
  editingName.value = ''
}

const handleRenameKeydown = (e: KeyboardEvent) => {
  if (e.key === 'Enter') {
    e.preventDefault()
    saveEdit()
  } else if (e.key === 'Escape') {
    e.preventDefault()
    cancelEdit()
  }
}

const deleteLayer = async () => {
  showDeleteConfirm.value = true
}

const confirmDelete = async () => {
  try {
    console.log('Deleting layer:', props.item.uuid, props.item.properties?.name)
    await layersStore.deleteOrgLayer({
      orgId,
      layerId: props.item.uuid
    })
    emit('deleteLayer', props.item.uuid)
    showDeleteConfirm.value = false
  } catch (error) {
    console.error('Error deleting layer:', error)
  }
}

const cancelDelete = () => {
  showDeleteConfirm.value = false
}

const focusOnGeometry = () => {
  const geometry = props.item?.geometry
  if (geometry) {
    const bounds = bbox(geometry)
    map.value.fitBounds(bounds, { padding: 300 })
  }
}

const focusOnChildGeometry = (child: any) => {
  const geometry = child?.geometry
  if (geometry) {
    const bounds = bbox(geometry)
    map.value.fitBounds(bounds, { padding: 300 })
  }
}

const childToDelete = ref<any>(null)

const deleteChildLayer = (child: any) => {
  childToDelete.value = child
}

const confirmDeleteChild = async () => {
  if (!childToDelete.value) return
  try {
    await layersStore.deleteOrgLayer({
      orgId,
      layerId: childToDelete.value.uuid
    })
    childToDelete.value = null
  } catch (error) {
    console.error('Error deleting child layer:', error)
  }
}

const cancelDeleteChild = () => {
  childToDelete.value = null
}

const moveUp = () => {
  layersStore.moveOrgLayerUp({
    orgId,
    layerId: props.item.uuid
  })
}

const moveDown = () => {
  layersStore.moveOrgLayerDown({
    orgId,
    layerId: props.item.uuid
  })
}

const navigateToLayerDetail = () => {
  if (isEditing.value || isTemp.value) return

  if (!isVisible.value) {
    toggleVisibility({ navigate: false })
  }

  router.push({
    name: 'LayerDetail',
    params: {
      org_id: orgId,
      layer_id: props.item.uuid
    }
  })
}

const handleItemClick = (e: MouseEvent) => {
  if (e.metaKey || e.ctrlKey) {
    e.preventDefault()
    layersStore.toggleLayerSelection({ orgId, layerCode: props.item.uuid })
    return
  }
  navigateToLayerDetail()
}

const groupSelection = () => {
  layersStore.groupSelectedLayers({ orgId })
}

const removeFromGroup = () => {
  layersStore.removeLayerFromGroup({ orgId, layerCode: props.item.uuid })
}

// Move-to-map (demo): reassign this layer to another map without dragging.
const maps = computed(() => mapsStore.getMaps(orgId))
const currentMap = computed(() => mapsStore.getMapForLayer(orgId, props.item.uuid))
const moveToMap = (mapId: string) => {
  mapsStore.moveLayerToMap({ orgId, layerCode: props.item.uuid, mapId })
}

// Copy stashes this layer on a clipboard; paste happens from a map section's
// right-click menu, which duplicates it as an independent layer on that map.
const copyLayer = () => {
  layersStore.copyLayer({ orgId, layerCode: props.item.uuid, name: props.item.properties?.name })
}

// Groups get a wide centre "inside" zone (middle 60%) so it's easy to drop into.
const getDropPosition = (element: HTMLElement, clientY: number, targetIsGroup: boolean): 'above' | 'below' | 'inside' => {
  const rect = element.getBoundingClientRect()
  const ratio = (clientY - rect.top) / rect.height
  if (targetIsGroup) {
    if (ratio < 0.2) return 'above'
    if (ratio > 0.8) return 'below'
    return 'inside'
  }
  return ratio < 0.5 ? 'above' : 'below'
}

// Spring-load: auto-expand a closed group when a drag lingers over it.
let expandTimer: ReturnType<typeof setTimeout> | null = null
const clearExpandTimer = () => {
  if (expandTimer !== null) {
    clearTimeout(expandTimer)
    expandTimer = null
  }
}

watchEffect((onCleanup) => {
  const currentElement = unrefElement(elRef)

  if (!currentElement)
    return

  const item = { ...props.item, id: props.item.uuid }

  const dndFunction = combine(
    draggable({
      element: currentElement,
      getInitialData: () => item,
      onDragStart: () => {
        if (isEditing.value) cancelEdit()
        isDragging.value = true
      },
      onDrop: () => {
        isDragging.value = false
      },
      onGenerateDragPreview({ nativeSetDragImage }) {
        setCustomNativeDragPreview({
          getOffset: pointerOutsideOfPreview({ x: '16px', y: '8px' }),
          render: ({ container }) => {
            const icon = isGroup.value ? Folder : getDataTypeIcon(props.item.properties?.data_type)
            const layerName = props.item.properties?.name?.trim() || 'Unnamed'

            return render(h(
              'div',
              {
                class: 'flex items-center gap-2 bg-background shadow-lg rounded-md text-sm font-medium px-3 py-2 border border-border',
                style: 'max-width: 200px'
              },
              [
                h(icon, { class: 'w-4 h-4 flex-shrink-0 text-muted-foreground opacity-85' }),
                h('span', { class: 'truncate' }, layerName)
              ]
            ), container)
          },
          nativeSetDragImage,
        })
      },
    }),

    dropTargetForElements({
      element: currentElement,
      getData: ({ input }) => {
        const pos = getDropPosition(currentElement, input.clientY, isDropTargetGroup.value)
        return {
          id: item.id,
          numericId: props.item.id,
          position: pos
        }
      },
      canDrop: ({ source }) => {
        return source.data.id !== item.id
      },
      onDrag: ({ self, source }) => {
        if (source.data.id !== item.id) {
          dropPosition.value = self.data.position as 'above' | 'below' | 'inside'
        }
      },
      onDragEnter: ({ source }) => {
        if (source.data.id !== item.id) {
          isDraggedOver.value = true
          if (isDropTargetGroup.value && !isExpanded.value) {
            expandTimer = setTimeout(() => { isExpanded.value = true }, 600)
          }
        }
      },
      onDragLeave: () => {
        isDraggedOver.value = false
        dropPosition.value = null
        clearExpandTimer()
      },
      onDrop: () => {
        isDraggedOver.value = false
        dropPosition.value = null
        clearExpandTimer()
      },
      getIsSticky: () => true,
    }),
  )

  onCleanup(() => dndFunction())
})

watchEffect((onCleanup) => {
  const cleanups: Array<() => void> = []

  children.value.forEach((child: any) => {
    const el = childElRefs[child.uuid]
    if (!el) return

    const cleanup = combine(
      draggable({
        element: el,
        getInitialData: () => ({ id: child.uuid, numericId: child.id, parentUuid: props.item.uuid }),
        onDragStart: () => { childDragging.value = child.uuid },
        onDrop: () => { childDragging.value = null },
        onGenerateDragPreview({ nativeSetDragImage }) {
          setCustomNativeDragPreview({
            getOffset: pointerOutsideOfPreview({ x: '16px', y: '8px' }),
            render: ({ container }) => {
              const icon = getDataTypeIcon(child.properties?.data_type)
              return render(h(
                'div',
                {
                  class: 'flex items-center gap-2 bg-background shadow-lg rounded-md text-sm font-medium px-3 py-2 border border-border',
                  style: 'max-width: 200px'
                },
                [
                  h(icon, { class: 'w-4 h-4 flex-shrink-0 text-muted-foreground opacity-85' }),
                  h('span', { class: 'truncate' }, child.properties?.name?.trim() || 'Unnamed')
                ]
              ), container)
            },
            nativeSetDragImage,
          })
        },
      }),
      dropTargetForElements({
        element: el,
        getData: ({ input }) => {
          const rect = el.getBoundingClientRect()
          const pos = (input.clientY - rect.top) / rect.height < 0.5 ? 'above' : 'below'
          return { id: child.uuid, numericId: child.id, parentUuid: props.item.uuid, position: pos }
        },
        canDrop: ({ source }) => source.data.id !== child.uuid,
        onDrag: ({ self }) => {
          childDropState.value = { uuid: child.uuid, pos: self.data.position as 'above' | 'below' }
        },
        onDragLeave: () => { childDropState.value = null },
        onDrop: () => { childDropState.value = null },
        getIsSticky: () => true,
      }),
    )
    cleanups.push(cleanup)
  })

  onCleanup(() => cleanups.forEach(c => c()))
})
</script>

<template>
  <ContextMenu>
    <ContextMenuTrigger as-child>
      <div class="group">
    <div
      ref="elRef"
      class="relative w-full px-3 py-1 rounded cursor-pointer transition-all duration-150"
      :class="{
        'opacity-60 scale-[0.98] shadow-md ring-2 ring-primary/30 bg-primary/5': isDragging,
        'bg-primary/10': route.params.layer_id === item.uuid && !isDragging && !isSelected,
        'bg-muted': isDraggedOver && !isDragging,
        'ring-2 ring-primary bg-primary/5': isSelected
      }"
      @click="handleItemClick"
      @mouseenter="isHovered = true"
      @mouseleave="isHovered = false"
    >
      <div class="flex justify-between items-center w-full">
        <div class="flex items-center min-w-0">
          <ChevronDown
            v-if="isGroup"
            class="w-4 h-4 mr-1 flex-shrink-0 text-muted-foreground transition-transform duration-150 cursor-pointer"
            :class="{ '-rotate-90': !isExpanded }"
            @click.stop="isExpanded = !isExpanded"
          />
          <component v-else :is="layerIcon" class="w-3 h-3 mr-2 flex-shrink-0 text-muted-foreground opacity-60" />
          <div class="flex items-center min-w-0">
            <component v-if="isGroup" :is="layerIcon" class="w-4 h-4 mr-2.5 flex-shrink-0 text-muted-foreground opacity-85" />
            <Spinner v-if="isProcessing" class="w-3.5 h-3.5 mr-1 flex-shrink-0" />
            <div v-if="isEditing" class="flex items-center gap-1 min-w-0" @click.stop>
              <Input
                ref="editInputRef"
                v-model="editingName"
                class="h-6 text-sm px-1 py-0 min-w-0"
                @click.stop
                @keydown="handleRenameKeydown"
              />
              <Button size="sm" variant="ghost" class="w-6 h-6 p-0 flex-shrink-0" @click.stop="saveEdit">
                <Check class="w-3.5 h-3.5 text-green-600" />
              </Button>
            </div>
            <h3 v-else class="truncate">{{ item.properties.name?.trim() || 'Unnamed' }}</h3>
          </div>
        </div>
        <div class="flex-shrink-0 flex items-center" @click.stop>
          <div :class="{
            'opacity-0': !isTouchLayout && !isHovered,
            'opacity-100': isTouchLayout || isHovered
          }">
            <DropdownMenu v-if="!isTemp">
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="ghost" class="w-6 h-6 p-0 rounded-full">
                  <Ellipsis class="w-4 h-4 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent class="w-48">
                <DropdownMenuGroup>
                  <DropdownMenuItem v-if="item.geometry" @click="focusOnGeometry" class="cursor-pointer">
                    <ScanEye class="w-4 h-4 mr-2" />
                    <span>Fly To</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem @click="moveUp" class="cursor-pointer">
                    <ArrowUp class="w-4 h-4 mr-2" />
                    <span>Move Up</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem @click="moveDown" class="cursor-pointer">
                    <ArrowDown class="w-4 h-4 mr-2" />
                    <span>Move Down</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem @click="startEditing" class="cursor-pointer">
                    <Pencil class="w-4 h-4 mr-2" />
                    <span>Rename</span>
                  </DropdownMenuItem>
                  <DropdownMenuSub v-if="maps.length > 1 && !isInGroup">
                    <DropdownMenuSubTrigger class="cursor-pointer">
                      <MapIcon class="w-4 h-4 mr-2" />
                      <span>Move to map</span>
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent>
                      <DropdownMenuItem
                        v-for="m in maps"
                        :key="m.id"
                        :disabled="m.id === currentMap?.id"
                        class="cursor-pointer"
                        @click="moveToMap(m.id)"
                      >
                        <span class="w-2.5 h-2.5 rounded-full mr-2 flex-shrink-0" :style="{ backgroundColor: m.color }" />
                        <span class="truncate">{{ m.name }}</span>
                      </DropdownMenuItem>
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem v-if="!isInGroup" @click="copyLayer" class="cursor-pointer">
                    <Copy class="w-4 h-4 mr-2" />
                    <span>Copy</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem @click="deleteLayer" class="text-red-500 cursor-pointer">
                    <Trash2 class="w-4 h-4 mr-2" />
                    <span>Delete</span>
                  </DropdownMenuItem>
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div
            :class="{
              'opacity-0': !isTouchLayout && !isHovered && !isVisible,
              'opacity-50': (isTouchLayout || isHovered) && !isVisible && (!isDataLoaded || isProcessing),
              'opacity-100': isVisible || ((isTouchLayout || isHovered) && isDataLoaded && !isProcessing)
            }"
            @click.stop
          >
            <TooltipProvider :delayDuration="450">
              <Tooltip>
                <TooltipTrigger as-child>
                  <Button size="sm" variant="ghost" class="w-6 h-6 p-0 rounded-full" @click.stop="toggleVisibility({ navigate: false })">
                    <EyeClosed class="text-foreground w-4 h-4" v-if="!isVisible" />
                    <Eye class="text-primary w-4 h-4" v-if="isVisible" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Visibility</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      </div>

      <!-- Drop indicator: line for above/below, full highlight for inside -->
      <div v-if="dropPosition === 'above'" class="absolute top-0 left-0 right-0 h-0.5 bg-primary rounded pointer-events-none z-10" />
      <div v-else-if="dropPosition === 'below'" class="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded pointer-events-none z-10" />
      <div v-else-if="dropPosition === 'inside'" class="absolute inset-0 rounded ring-2 ring-inset ring-primary bg-primary/10 pointer-events-none z-10 flex items-center justify-center">
        <span class="text-xs font-semibold text-primary bg-background/90 px-2 py-0.5 rounded-full border border-primary/40 shadow-sm select-none">
          Add to group
        </span>
      </div>
    </div>

    <!-- Group children -->
    <div v-if="isGroup && children.length" v-show="isExpanded" class="ml-7 pl-2 border-l border-border/60 mt-0.5 space-y-0.5">
      <div
        v-for="child in children"
        :key="child.uuid"
        :ref="(el: any) => { if (el) childElRefs[child.uuid] = el; else delete childElRefs[child.uuid] }"
        class="group/child relative w-full px-3 py-1 rounded cursor-pointer transition-colors duration-150 hover:bg-muted/60"
        :class="{
          'bg-primary/10': route.params.layer_id === child.uuid,
          'opacity-50': childDragging === child.uuid
        }"
        @click="navigateToChild(child)"
      >
        <div class="flex justify-between items-center w-full">
          <div class="flex items-center min-w-0 gap-2.5">
            <component
              :is="getDataTypeIcon(child.properties?.data_type)"
              class="w-4 h-4 flex-shrink-0 text-muted-foreground opacity-85"
            />
            <span class="truncate text-sm">{{ child.properties?.name || 'Unnamed' }}</span>
          </div>
          <div class="flex items-center flex-shrink-0" @click.stop>
            <div class="opacity-0 group-hover/child:opacity-100 transition-opacity">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="ghost" class="w-6 h-6 p-0 rounded-full">
                    <Ellipsis class="w-4 h-4 text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent class="w-48">
                  <DropdownMenuGroup>
                    <DropdownMenuItem v-if="child.geometry" @click="focusOnChildGeometry(child)" class="cursor-pointer">
                      <ScanEye class="w-4 h-4 mr-2" />
                      <span>Fly To</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem @click="navigateToChild(child)" class="cursor-pointer">
                      <Pencil class="w-4 h-4 mr-2" />
                      <span>Rename</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem @click="deleteChildLayer(child)" class="text-red-500 cursor-pointer">
                      <Trash2 class="w-4 h-4 mr-2" />
                      <span>Delete</span>
                    </DropdownMenuItem>
                  </DropdownMenuGroup>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <Button
              size="sm" variant="ghost"
              class="w-6 h-6 p-0 rounded-full flex-shrink-0 transition-opacity"
              :class="childVisible[child.uuid] ? 'opacity-100' : 'opacity-0 group-hover/child:opacity-100'"
              @click.stop="toggleChildVisibility(child)"
            >
              <EyeClosed v-if="!childVisible[child.uuid]" class="w-4 h-4 text-foreground" />
              <Eye v-else class="w-4 h-4 text-primary" />
            </Button>
          </div>
        </div>

        <!-- Drop indicators -->
        <div v-if="childDropState?.uuid === child.uuid && childDropState?.pos === 'above'"
             class="absolute top-0 left-0 right-0 h-0.5 bg-primary rounded pointer-events-none z-10" />
        <div v-else-if="childDropState?.uuid === child.uuid && childDropState?.pos === 'below'"
             class="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded pointer-events-none z-10" />

      </div>
    </div>
      </div>
    </ContextMenuTrigger>

    <ContextMenuContent class="w-52">
        <ContextMenuItem v-if="!isGroup" :disabled="selectedLayerCount < 2" @click="groupSelection">
          Group selection
          <ContextMenuShortcut>⌘G</ContextMenuShortcut>
        </ContextMenuItem>
        <ContextMenuItem v-if="isInGroup" @click="removeFromGroup">
          Remove from group
          <ContextMenuShortcut>⇧⌘G</ContextMenuShortcut>
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem v-if="item.geometry" @click="focusOnGeometry">
          <ScanEye class="w-4 h-4 mr-2" />
          Fly To
        </ContextMenuItem>
        <ContextMenuItem @click="startEditing">
          <Pencil class="w-4 h-4 mr-2" />
          Rename
        </ContextMenuItem>
        <ContextMenuSub v-if="maps.length > 1 && !isInGroup">
          <ContextMenuSubTrigger>
            <MapIcon class="w-4 h-4 mr-2" />
            Move to map
          </ContextMenuSubTrigger>
          <ContextMenuSubContent>
            <ContextMenuItem
              v-for="m in maps"
              :key="m.id"
              :disabled="m.id === currentMap?.id"
              @click="moveToMap(m.id)"
            >
              <span class="w-2.5 h-2.5 rounded-full mr-2 flex-shrink-0" :style="{ backgroundColor: m.color }" />
              {{ m.name }}
            </ContextMenuItem>
          </ContextMenuSubContent>
        </ContextMenuSub>
        <ContextMenuSeparator />
        <ContextMenuItem v-if="!isInGroup" @click="copyLayer">
          <Copy class="w-4 h-4 mr-2" />
          Copy
        </ContextMenuItem>
        <ContextMenuItem class="text-red-500" @click="deleteLayer">
          <Trash2 class="w-4 h-4 mr-2" />
          Delete
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>

  <!-- Delete Confirmation Dialog -->
  <Dialog v-model:open="showDeleteConfirm">
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Delete Layer</DialogTitle>
        <DialogDescription>
          Are you sure you want to delete the layer "{{ props.item.properties?.name }}"?
          This action cannot be undone.
        </DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <Button variant="outline" @click="cancelDelete">Cancel</Button>
        <Button variant="destructive" @click="confirmDelete">Delete</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>

  <!-- Child Delete Confirmation Dialog -->
  <Dialog :open="!!childToDelete" @update:open="(val: boolean) => { if (!val) cancelDeleteChild() }">
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Delete Layer</DialogTitle>
        <DialogDescription>
          Are you sure you want to delete the layer "{{ childToDelete?.properties?.name }}"?
          This action cannot be undone.
        </DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <Button variant="outline" @click="cancelDeleteChild">Cancel</Button>
        <Button variant="destructive" @click="confirmDeleteChild">Delete</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
